import { Button, InputNumber, Radio, Select, Switch, Table, Tag, TimePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle, MyModal } from "../../components/styled-components";
import { GET_BATCH_UP_CONSUMER, PATCH_BATCH_UP_CONSUMER } from "../../services";
import { BATCH_SCHEDULE_LIST, BATCH_SCHEDULE_UPDATE } from "../../services/path";

const MINUTE_STEP = 5;
const MIN_PERIOD_VALUE = 5;

enum SCHEDULE_TYPE {
  CUSTOM = "custom",
  INTERVAL = "interval",
}

enum PERIOD_UNIT {
  MINUTE = "minute",
  HOUR = "hour",
}

enum DEFAULT_SCHEDULE {
  START = "06:00",
  END = "22:00",
}

const TIME_FORMAT = "HH:mm";

interface ScheduleConfig {
  type: SCHEDULE_TYPE;
  times?: string[];
  value?: number;
  unit?: PERIOD_UNIT;
  start?: string;
  end?: string;
}

interface CronjobConfig {
  id: number;
  platf_id: number;
  platf_name: string;
  logistic: string;
  is_active: boolean;
  schedule_config: ScheduleConfig | null;
}

interface ScheduleForm {
  scheduleType: SCHEDULE_TYPE;
  customTimes: string[];
  pendingTime: string;
  periodValue: number;
  periodUnit: PERIOD_UNIT;
  startTime: string;
  endTime: string;
}

const defaultForm: ScheduleForm = {
  scheduleType: SCHEDULE_TYPE.CUSTOM,
  customTimes: [],
  pendingTime: "",
  periodValue: 10,
  periodUnit: PERIOD_UNIT.MINUTE,
  startTime: DEFAULT_SCHEDULE.START,
  endTime: DEFAULT_SCHEDULE.END,
};

const disabledMinutesNotMultipleOfFive = (): number[] =>
  Array.from({ length: 60 }, (_, minute) => minute).filter((minute) => minute % 5 !== 0);

const formatScheduleSummary = (s: ScheduleConfig | null): string => {
  if (!s) return "—";
  if (s.type === SCHEDULE_TYPE.CUSTOM) {
    const times = s.times ?? [];
    return times.length > 0 ? times.join(", ") : "ยังไม่มีเวลา";
  }
  if (s.type === SCHEDULE_TYPE.INTERVAL) {
    const unit = s.unit === PERIOD_UNIT.MINUTE ? "นาที" : "ชั่วโมง";
    return `ทุก ${s.value} ${unit} ${s.start}–${s.end}`;
  }
  return "—";
};

const buildScheduleConfig = (form: ScheduleForm): ScheduleConfig => {
  if (form.scheduleType === SCHEDULE_TYPE.CUSTOM) {
    return { type: SCHEDULE_TYPE.CUSTOM, times: form.customTimes };
  }
  return {
    type: SCHEDULE_TYPE.INTERVAL,
    value: form.periodValue,
    unit: form.periodUnit,
    start: form.startTime,
    end: form.endTime,
  };
};

const formFromConfig = (s: ScheduleConfig | null): ScheduleForm => {
  if (!s) return defaultForm;
  if (s.type === SCHEDULE_TYPE.CUSTOM) {
    return { ...defaultForm, scheduleType: SCHEDULE_TYPE.CUSTOM, customTimes: s.times ?? [] };
  }
  if (s.type === SCHEDULE_TYPE.INTERVAL) {
    return {
      ...defaultForm,
      scheduleType: SCHEDULE_TYPE.INTERVAL,
      periodValue: s.value ?? 10,
      periodUnit: s.unit ?? PERIOD_UNIT.MINUTE,
      startTime: s.start ?? DEFAULT_SCHEDULE.START,
      endTime: s.end ?? DEFAULT_SCHEDULE.END,
    };
  }
  return defaultForm;
};

const BatchSchedulePage = () => {
  const [data, setData] = useState<CronjobConfig[]>([]);
  const [pending, setPending] = useState<{ record: CronjobConfig; checked: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [scheduleTargets, setScheduleTargets] = useState<CronjobConfig[]>([]);
  const [form, setForm] = useState<ScheduleForm>(defaultForm);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchData = async () => {
    try {
      const result = await GET_BATCH_UP_CONSUMER(BATCH_SCHEDULE_LIST);
      setData(result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleClick = (record: CronjobConfig, checked: boolean) => {
    setPending({ record, checked });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await PATCH_BATCH_UP_CONSUMER(BATCH_SCHEDULE_UPDATE(pending.record.id), {
        is_active: pending.checked,
        schedule_config: pending.record.schedule_config,
      });
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  const handleCancel = () => setPending(null);

  const openScheduleModal = (record: CronjobConfig) => {
    setScheduleTargets([record]);
    setForm(formFromConfig(record.schedule_config));
  };

  const openScheduleModalBulk = () => {
    const targets = data.filter((r) => selectedRowKeys.includes(r.id));
    setScheduleTargets(targets);
    setForm(defaultForm);
  };

  const closeScheduleModal = () => {
    setScheduleTargets([]);
    setForm(defaultForm);
  };

  const handleSaveSchedule = async () => {
    if (scheduleTargets.length === 0) return;
    setSavingSchedule(true);
    try {
      const scheduleConfig = buildScheduleConfig(form);
      await Promise.all(
        scheduleTargets.map((target) =>
          PATCH_BATCH_UP_CONSUMER(BATCH_SCHEDULE_UPDATE(target.id), {
            is_active: target.is_active,
            schedule_config: scheduleConfig,
          }),
        ),
      );
      fetchData();
      closeScheduleModal();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingSchedule(false);
    }
  };

  const addCustomTime = () => {
    const t = form.pendingTime;
    if (!t || form.customTimes.includes(t)) return;
    const sorted = [...form.customTimes, t].sort();
    setForm((prev) => ({ ...prev, customTimes: sorted, pendingTime: "" }));
  };

  const removeCustomTime = (t: string) => {
    setForm((prev) => ({ ...prev, customTimes: prev.customTimes.filter((x) => x !== t) }));
  };

  const scheduleModalOpen = scheduleTargets.length > 0;

  const scheduleModalTitle = (() => {
    if (scheduleTargets.length === 0) return "";
    if (scheduleTargets.length === 1) {
      const r = scheduleTargets[0];
      return `ตั้งค่าตารางเวลา: ${r.platf_name}${r.logistic ? ` · ${r.logistic}` : ""}`;
    }
    return `ตั้งค่าตารางเวลา: ${scheduleTargets.length} รายการ`;
  })();

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as number[]),
  };

  const columns = [
    {
      title: <div style={{ textAlign: "center" }}>#</div>,
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => (
        <div style={{ textAlign: "center" }}>{index + 1}</div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Platform</div>,
      dataIndex: "platf_name",
      key: "platf_name",
      render: (text: string) => <div style={{ textAlign: "center" }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: "center" }}>Logistic</div>,
      dataIndex: "logistic",
      key: "logistic",
      render: (text: string) => <div style={{ textAlign: "center" }}>{text || "-"}</div>,
    },
    {
      title: (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
          <span>ตารางเวลา</span>
          <Tooltip
            title={
              selectedRowKeys.length === 0
                ? "เลือกรายการก่อน"
                : `ตั้งเวลาสำหรับ ${selectedRowKeys.length} รายการ`
            }
          >
            <Button
              type="primary"
              size="small"
              disabled={selectedRowKeys.length === 0}
              onClick={openScheduleModalBulk}
            >
              ตั้งเวลา {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
            </Button>
          </Tooltip>
        </div>
      ),
      key: "schedule",
      render: (_: any, record: CronjobConfig) => (
        <div style={{ textAlign: "center" }}>
          <Button
            type="link"
            onClick={() => openScheduleModal(record)}
            style={{
              maxWidth: 300,
              display: "inline-block",
              width: "100%",
              height: "auto",
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.4,
            }}
          >
            {formatScheduleSummary(record.schedule_config)}
          </Button>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Status</div>,
      dataIndex: "is_active",
      key: "is_active",
      render: (value: boolean, record: CronjobConfig) => (
        <div style={{ textAlign: "center" }}>
          <Switch
            checked={value}
            onChange={(checked) => handleToggleClick(record, checked)}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </div>
      ),
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>Batch Schedule</h2>
        </HeaderTitle>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{ pageSize: 10, position: ["bottomRight"] }}
        />
      </ContentBox>

      <MyModal
        open={!!pending}
        title="ยืนยันการเปลี่ยนแปลง"
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={saving}
      >
        <p>
          คุณต้องการ<b>{pending?.checked ? "เปิด" : "ปิด"}</b>การรัน Batch อัตโนมัติ
        </p>
        <p>
          Platform: <b>{pending?.record.platf_name}</b>
          {pending?.record.logistic && (
            <>
              {" "}
              / Logistic: <b>{pending.record.logistic}</b>
            </>
          )}
        </p>
      </MyModal>

      <MyModal
        open={scheduleModalOpen}
        title={scheduleModalTitle}
        onOk={handleSaveSchedule}
        onCancel={closeScheduleModal}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={savingSchedule}
        width={480}
      >
        {scheduleTargets.length > 1 && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 12px",
              background: "#F5F5F5",
              borderRadius: 6,
              fontSize: 13,
              color: "#666666",
            }}
          >
            จะนำไปใช้กับ:{" "}
            {scheduleTargets
              .map((r) => `${r.platf_name}${r.logistic ? ` · ${r.logistic}` : ""}`)
              .join(", ")}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>ประเภท</div>
          <Radio.Group
            value={form.scheduleType}
            onChange={(e) => setForm((prev) => ({ ...prev, scheduleType: e.target.value }))}
          >
            <Radio value={SCHEDULE_TYPE.CUSTOM}>กำหนดเอง</Radio>
            <Radio value={SCHEDULE_TYPE.INTERVAL}>ช่วงเวลา (Interval)</Radio>
          </Radio.Group>
        </div>

        {form.scheduleType === SCHEDULE_TYPE.CUSTOM ? (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>เวลาที่รัน</div>
            <div
              style={{
                minHeight: 40,
                marginBottom: 12,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
              }}
            >
              {form.customTimes.length === 0 ? (
                <span style={{ color: "#BBBBBB" }}>ยังไม่มีเวลา</span>
              ) : (
                form.customTimes.map((t) => (
                  <Tag key={t} closable onClose={() => removeCustomTime(t)}>
                    {t}
                  </Tag>
                ))
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <TimePicker
                format={TIME_FORMAT}
                value={form.pendingTime ? dayjs(form.pendingTime, TIME_FORMAT) : null}
                minuteStep={MINUTE_STEP}
                hideDisabledOptions
                inputReadOnly
                disabledTime={() => ({ disabledMinutes: disabledMinutesNotMultipleOfFive })}
                onChange={(val) =>
                  setForm((prev) => ({ ...prev, pendingTime: val ? val.format(TIME_FORMAT) : "" }))
                }
                placeholder="เลือกเวลา"
                style={{ width: 130 }}
              />
              <Button onClick={addCustomTime} disabled={!form.pendingTime}>
                + เพิ่มเวลา
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>รันทุก</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <InputNumber
                  min={form.periodUnit === PERIOD_UNIT.MINUTE ? MIN_PERIOD_VALUE : 1}
                  max={1440}
                  value={form.periodValue}
                  onChange={(val) =>
                    setForm((prev) => ({
                      ...prev,
                      periodValue:
                        val ?? (prev.periodUnit === PERIOD_UNIT.MINUTE ? MIN_PERIOD_VALUE : 1),
                    }))
                  }
                  style={{ width: 80 }}
                />
                <Select
                  value={form.periodUnit}
                  onChange={(val) =>
                    setForm((prev) => ({
                      ...prev,
                      periodUnit: val,
                      periodValue:
                        val === PERIOD_UNIT.MINUTE && prev.periodValue < MIN_PERIOD_VALUE
                          ? MIN_PERIOD_VALUE
                          : prev.periodValue,
                    }))
                  }
                  style={{ width: 110 }}
                  options={[
                    { value: PERIOD_UNIT.MINUTE, label: "นาที" },
                    { value: PERIOD_UNIT.HOUR, label: "ชั่วโมง" },
                  ]}
                />
              </div>
            </div>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>ช่วงเวลา</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <TimePicker
                  format={TIME_FORMAT}
                  value={form.startTime ? dayjs(form.startTime, TIME_FORMAT) : null}
                  minuteStep={MINUTE_STEP}
                  hideDisabledOptions
                  inputReadOnly
                  disabledTime={() => ({ disabledMinutes: disabledMinutesNotMultipleOfFive })}
                  onChange={(val) =>
                    setForm((prev) => ({ ...prev, startTime: val ? val.format(TIME_FORMAT) : "" }))
                  }
                  style={{ width: 120 }}
                />
                <span style={{ color: "#666666" }}>ถึง</span>
                <TimePicker
                  format={TIME_FORMAT}
                  value={form.endTime ? dayjs(form.endTime, TIME_FORMAT) : null}
                  minuteStep={MINUTE_STEP}
                  hideDisabledOptions
                  inputReadOnly
                  disabledTime={() => ({ disabledMinutes: disabledMinutesNotMultipleOfFive })}
                  onChange={(val) =>
                    setForm((prev) => ({ ...prev, endTime: val ? val.format(TIME_FORMAT) : "" }))
                  }
                  style={{ width: 120 }}
                />
              </div>
            </div>
          </div>
        )}
      </MyModal>
    </LoginedLayout>
  );
};

export default BatchSchedulePage;
